import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft } from "lucide-react";

/**
 * Placeholder terms of service. See PrivacyNotice.jsx for the parallel privacy notice and
 * its caveats — the same "draft, not legally reviewed" rule applies here.
 *
 * Unlike the privacy notice, most of this page states things that are simply true about
 * how the product is built (CLAUDE.md's blocking rules, ROADMAP.md's "never" list) — those
 * sections are not placeholders, they are accurate descriptions of product behaviour that
 * happen to also belong in terms of service. Only the account/liability/dispute mechanics
 * are marked PLACEHOLDER, because those are genuinely undecided.
 */

function Section({ title, children }) {
    return (
        <section className="mt-8">
            <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
            <div className="mt-2 text-sm text-slate-700 leading-relaxed space-y-2">{children}</div>
        </section>
    );
}

function Placeholder({ children }) {
    return (
        <span className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-mono text-[13px]">
            {children}
        </span>
    );
}

export default function TermsOfService() {
    return (
        <div className="min-h-screen bg-[#F9FAFB]">
            <div className="max-w-2xl mx-auto px-6 py-12">
                <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                </Link>

                <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 flex gap-3" data-testid="terms-draft-banner">
                    <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <strong>Draft — not yet reviewed by a solicitor.</strong> This is a starting point,
                        not a finished terms of service. See{" "}
                        <code className="text-xs">planning/LAUNCH-READINESS-TRIAL.md</code>.
                    </div>
                </div>

                <h1 className="font-heading text-2xl font-bold text-slate-900 mt-8">Terms of service</h1>
                <p className="text-sm text-slate-500 mt-1">Draft — last edited 2026-08-24, not yet legally reviewed.</p>

                <Section title="What this is">
                    <p>
                        A tool for residents and the resident-controlled management company of an estate
                        to understand and verify their own service charges — whether or not they employ a
                        managing agent. It is not property-management software sold to agents, and it does
                        not act on your behalf without your say-so.
                    </p>
                </Section>

                <Section title="No legal advice">
                    <p>
                        This product surfaces evidence, computes deadlines, and drafts correspondence for
                        your review. It does not give legal advice, does not tell you whether a charge is
                        unlawful, and does not predict the outcome of a dispute. Every letter it drafts is
                        watermarked as a draft and requires you to review and send it yourself — nothing is
                        sent automatically.
                    </p>
                </Section>

                <Section title="Payment">
                    <p>
                        Any subscription you pay is for use of this software. This product does not hold,
                        route, or collect your estate's service charge money on your behalf, and never
                        will — that stays between you, your management company, and your bank.
                    </p>
                    <p>
                        During a trial, billing may be handled manually rather than through an automated
                        payment provider — an operator will confirm your access rather than a card
                        transaction confirming it automatically.
                    </p>
                </Section>

                <Section title="Your account, and undecided terms">
                    <p>
                        You are responsible for keeping your password confidential. You must not attempt
                        to access another resident's or estate's data.
                    </p>
                    <ul className="list-disc pl-5 space-y-1.5">
                        <li>Liability limits: <Placeholder>[PLACEHOLDER — not yet drafted]</Placeholder></li>
                        <li>Governing law and jurisdiction: <Placeholder>[PLACEHOLDER — expected to be England and Wales, not yet confirmed]</Placeholder></li>
                        <li>How to end this agreement, and what happens to your data if you do: <Placeholder>[PLACEHOLDER]</Placeholder></li>
                        <li>Changes to these terms: <Placeholder>[PLACEHOLDER]</Placeholder></li>
                    </ul>
                </Section>

                <p className="text-xs text-slate-400 mt-10 pt-6 border-t border-slate-200">
                    See also: <Link to="/privacy-notice" className="underline">Privacy notice</Link> (also a draft).
                </p>
            </div>
        </div>
    );
}
